import React, { FC, memo, useEffect, useState } from "react"
import { ApolloError } from "@apollo/client"
import * as Sentry from "@sentry/react"
import moment from "moment"
import { Booker, BookerAttendeeInput, BookerPaymentMethodInput, Gender, GetUpdatedBookerDocument, useUpdateBookerMutation } from "../../generated/graphql"
import { isDefined, RequireUndefined } from "../../helpers/typeScriptHelpers"
import { v4 } from "uuid"
import css from "./CreateEditAttendee.module.css"
import { Formik, FormikProps } from "formik"
import { useTranslation } from "react-i18next"
import { BookerAttendeeInputValidationSchema } from "../../containers/CreateBooking/Steps/validationSchemas/BookerAttendeeInputValidationSchema"
import { findDuplicateAttendee, hasSameIdentity, toDateOnly } from "../SelectAttendeeTable/findDuplicateAttendee"
import { attendeeCheck } from "../SelectAttendeeTable/checkAttendee"
import { Alert, Button, Form, Select, Input, message, Space } from "antd"
import { getMobileOperatingSystem, margin } from "../../styles/layout"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import LayoutForm from "../LayoutForm/LayoutForm"
import { omit } from "../../helpers/omit"
import LoadingContent from "../LoadingContent/LoadingContent"
import BirthdayInput from "../BirthdayInput/BirthdayInput"
// import { cleanupGraphQLData } from "../../helpers/cleanupGraphQLData"

type CreateEditAttendeeProps = {
    onCancel?: () => void
    onOk?: (data: BookerAttendeeInput) => void
    // Dubletten-Umleitung (sc-3066): Wird gerufen, wenn der Nutzer statt einer
    // Neuanlage den bereits vorhandenen Teilnehmer verwenden möchte.
    onSelectExisting?: (attendee: Pick<BookerAttendeeInput, "id" | "firstname">) => void
    initialValues?: BookerAttendeeInput
    editMode?: boolean
    attendeeToEdit?: BookerAttendeeInput
    booker: Booker
    courseId?: string
    showAlert?: boolean
}

export const getDefaultInitialValues = (courseId?: string): RequireUndefined<BookerAttendeeInput> & { courseId?: string } => ({
    id: v4(),
    gender: Gender.NotDefined,
    firstname: "",
    lastname: "",
    birthday: undefined,
    customer: { id: v4() },
    member: false,
    defaultPaymentMethod: undefined,
    bookings: undefined,
    addition: "",
    annotation: "",
    courseId,
    badges: undefined,
    bookingsKo9: null,
    deletable: undefined,
    hasBillingAddress: undefined,
    billingCompanyName: undefined,
    billingFirstname: undefined,
    billingLastname: undefined,
    billingStreet: undefined,
    billingPostCode: undefined,
    billingCity: undefined,
    billingAddition: undefined,
})

const CreateEditAttendee: FC<CreateEditAttendeeProps> = ({ onCancel = Function.prototype, onOk = Function.prototype, onSelectExisting, editMode, attendeeToEdit, booker, courseId, showAlert }) => {
    const oS = getMobileOperatingSystem()
    const { t } = useTranslation()

    const [updateBookerDb, { loading: updatingBooker }] = useUpdateBookerMutation()

    const [initialValues, setInitialValues] = useState<BookerAttendeeInput & { courseId?: string }>(getDefaultInitialValues(courseId))

    // Umleitung auf den Bestands-Teilnehmer: bevorzugt den dedizierten Handler,
    // fällt sonst auf onOk zurück (gleiche Selektions-Mechanik im Aufrufer)
    const handleUseExisting = (attendee: BookerAttendeeInput) => {
        if (onSelectExisting) {
            onSelectExisting(attendee)
        } else {
            onOk(attendee)
        }
    }

    const formatBirthday = (birthday: BookerAttendeeInput["birthday"]): string => {
        const dateOnly = toDateOnly(birthday)
        return dateOnly ? moment(dateOnly, "YYYY-MM-DD").format("DD.MM.YYYY") : ""
    }

    // Edit-Modus: Solange Name + Geburtsdatum unverändert sind, bleibt der Guard
    // aus — sonst wäre ein Bestands-Klon (Import-Altlast) nie bearbeitbar, weil
    // sein Zwilling immer matcht. Bei geänderter Identität greift der Guard normal.
    const checkForDuplicate = (candidate: BookerAttendeeInput): ReturnType<typeof findDuplicateAttendee> => {
        if (editMode && isDefined(attendeeToEdit) && hasSameIdentity(candidate, attendeeToEdit)) {
            return undefined
        }
        return findDuplicateAttendee(booker?.attendees, candidate, {
            excludeId: attendeeToEdit?.id ?? candidate.id,
            courseId,
        })
    }

    useEffect(() => {
        if (isDefined(attendeeToEdit)) {
            // courseId nur setzen wenn vorhanden — im Account-Flow gibt es keinen Kurs,
            // das Formular muss trotzdem mit den Teilnehmerdaten befüllt werden
            setInitialValues(isDefined(courseId) ? { ...attendeeToEdit, courseId } : attendeeToEdit)
        }
    }, [attendeeToEdit, courseId])

    const updateBookersAttendee = async (
        newAttendeeData: BookerAttendeeInput,
        setSubmitting: (isSubmitting: boolean) => void,
        resetForm: (nextState?: Partial<FormikProps<BookerAttendeeInput>>) => void
    ) => {
        try {
            const currentBookerData = booker

            // Create a mutable copy of the booker data without the omitted entries
            const omitedEntries: Array<Extract<keyof Booker, string>> = ["createDate", "updateDate", "customerId", "addition", "annotation", "createdByUserId", "updateDate", "createdBy"]
            const updateBookerData = {
                ...omit(["bookerId", ...omitedEntries], currentBookerData),
                customer: { id: currentBookerData.customerId },
                paymentMethods: Array.isArray(currentBookerData.paymentMethods) ? [...currentBookerData.paymentMethods] : [],
            }

            // IF THE BOOKER HAS NO PAYMENTMETHODS, CREATE ONE (cash)
            if (updateBookerData.paymentMethods.length === 0) {
                const newDefaultPaymentMethodId = v4()
                updateBookerData.paymentMethods = [
                    {
                        id: newDefaultPaymentMethodId,
                        cash: true,
                    } as BookerPaymentMethodInput,
                ]
            }

            let updatedAttendeeData = omit(["bookerId", "courseId", ...omitedEntries], newAttendeeData)

            // Initialize attendees if undefined
            if (!updateBookerData.attendees) {
                updateBookerData.attendees = []
            }

            if (editMode && isDefined(attendeeToEdit?.id)) {
                // Edit: den bestehenden Eintrag per ID ERSETZEN statt erneut anhängen —
                // sonst enthält die Mutation dieselbe Teilnehmer-ID zweimal. bookings und
                // defaultPaymentMethod NICHT überschreiben: bookings: [] würde über die
                // TypeORM-Cascade die Buchungs-Beziehungen entkoppeln (vgl. Backend-Fix
                // 4f2583d80); der Bestand aus dem geladenen Booker bleibt unangetastet.
                const attendeeFormFields = omit(["bookings", "defaultPaymentMethod"], updatedAttendeeData)
                updateBookerData.attendees = updateBookerData.attendees.map((existing: BookerAttendeeInput) =>
                    existing.id === attendeeToEdit?.id ? { ...existing, ...attendeeFormFields } : existing
                )
                updatedAttendeeData = updateBookerData.attendees.find((existing: BookerAttendeeInput) => existing.id === attendeeToEdit?.id) ?? updatedAttendeeData
            } else {
                // Neuanlage: neuer Teilnehmer hat noch keine Buchungen; Standardzahlungsart
                // ist die erste (ggf. gerade angelegte) Zahlungsmethode des Bookers.
                updatedAttendeeData = {
                    ...updatedAttendeeData,
                    defaultPaymentMethod: { id: updateBookerData.paymentMethods[0].id },
                    bookings: [],
                }
                updateBookerData.attendees = [...updateBookerData.attendees, updatedAttendeeData]
            }

            // Remove __typename from the data
            // const cleanedBookerData = cleanupGraphQLData(updateBookerData)
            await updateBookerDb({
                variables: {
                    updateBookerData,
                    fromEndUserApp: true,
                },
                refetchQueries: [{ query: GetUpdatedBookerDocument, variables: { skip: !booker?.id, id: booker?.id ?? "" } }],
            }).then(() => {
                if (!updatingBooker) {
                    resetForm()
                    onOk(updatedAttendeeData)
                }
            })
        } catch (error) {
            // Künftiger Backend-Guard (sc-3066): strukturierter Fehler bei Dubletten-Anlage.
            // Bevorzugt wird die vom Backend gelieferte duplicateAttendeeId; der
            // Client-Match ist der Fallback (greift nur, wenn das Backend großzügiger
            // matcht als der vorgeschaltete Client-Check, z. B. bei Diakritika).
            // Duck-Typing statt instanceof ApolloError (Projekt-Pattern, vgl.
            // getGraphQLErrorMessage) — robust gegen doppelte Modul-Instanzen.
            const errorExtensions = (error as ApolloError)?.graphQLErrors?.[0]?.extensions
            if (errorExtensions?.code === "DUPLICATE_ATTENDEE") {
                const duplicateAttendeeId = typeof errorExtensions.duplicateAttendeeId === "string" ? errorExtensions.duplicateAttendeeId : undefined
                const byIdAttendee = duplicateAttendeeId ? booker?.attendees?.find((attendee) => attendee.id === duplicateAttendeeId) : undefined
                const byMatch = findDuplicateAttendee(booker?.attendees, newAttendeeData, {
                    excludeId: newAttendeeData.id,
                    courseId,
                })
                // Bestands-Teilnehmer nur als Umleitungsziel verwenden, wenn er noch
                // buchbar ist — sonst ließe die Server-Tür genau die Doppelbuchung
                // (Ryan-Fall) wieder herein, die der Client-Pfad bewusst blockiert.
                // Symmetrisch zum byMatch-Zweig, der bereits gegen allreadyBooked prüft.
                const byId = isDefined(byIdAttendee) && !attendeeCheck(byIdAttendee, courseId).allreadyBooked ? byIdAttendee : undefined
                const existingAttendee = byId ?? (isDefined(byMatch) && !byMatch.allreadyBooked ? byMatch.attendee : undefined)
                if (isDefined(existingAttendee)) {
                    handleUseExisting(existingAttendee)
                    return
                }
                // Treffer existiert, ist aber bereits in diesen Kurs gebucht: keine
                // Umleitung, sondern dieselbe Warnung wie im Client-Pfad — und kein
                // Sentry-Error, da fachlich erwartet (keine echte Speicher-Störung).
                const alreadyBookedAttendee = byIdAttendee ?? byMatch?.attendee
                if (isDefined(alreadyBookedAttendee)) {
                    message.warning(t("createEditAttendee.duplicate.alreadyBookedInCourse", { firstname: alreadyBookedAttendee.firstname }))
                    return
                }
            }
            console.error(`Error on updateBooker(updateBookersAttendee): ${error}`)
            Sentry.captureException(error)
            message.error(t("createEditAttendee.errors.saveFailed"))
            return error
        }
    }

    return (
        <Formik<BookerAttendeeInput>
            validateOnMount
            validateOnBlur
            initialValues={initialValues}
            onSubmit={(data, { resetForm, setSubmitting }) => {
                // Sicherheitsnetz (sc-3066): Der Submit-Button ist bei einem Duplikat
                // bereits deaktiviert — hier wird vor resetForm() trotzdem geprüft,
                // damit Eingaben bei einem Race nicht verloren gehen.
                if (isDefined(checkForDuplicate(data))) {
                    return
                }
                // Kein resetForm() vor der Mutation — bei einem Serverfehler bleiben
                // die Eingaben erhalten; Reset erfolgt erst im Erfolgspfad.
                updateBookersAttendee(data, setSubmitting, resetForm)
            }}
            validationSchema={BookerAttendeeInputValidationSchema}
            enableReinitialize={oS !== "Android"}
        >
            {(formikProps) => {
                const { values, handleSubmit, resetForm, isValid } = formikProps
                const { handleTextInputChange, handleSelectInputChange, validateStatus, handleBlur, help, safeSetFieldValue } = createInputHelpers(formikProps)

                // Live-Dubletten-Check (sc-3066): Selbstausnahme im Edit-Modus über
                // attendeeToEdit.id — auf Android gilt enableReinitialize=false, dort
                // kann values.id noch die Create-uuid enthalten.
                const duplicateMatch = checkForDuplicate(values)

                const handleCancle = () => {
                    resetForm()
                    onCancel()
                }

                const handleOk = () => {
                    handleSubmit()
                }

                if (updatingBooker) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                paddingTop: margin.s,
                            }}
                        >
                            <LoadingContent />
                            <div style={{ paddingTop: margin.s }}>Teilnehmer wird gespeichert</div>
                        </div>
                    )
                }

                return (
                    <LayoutForm columns={1}>
                        <Form.Item style={{ gridColumn: "span 1" }} label='Geschlecht' validateStatus={validateStatus("gender")} help={help("gender")} required>
                            <Select<Gender> data-testid='AttendeeCreateModalGender' onChange={handleSelectInputChange("gender")} onBlur={handleBlur("gender")} value={values.gender}>
                                <Select.Option key={Gender.NotDefined} value={Gender.NotDefined}>
                                    Keine Angabe
                                </Select.Option>
                                <Select.Option key={Gender.Female} value={Gender.Female}>
                                    Weiblich
                                </Select.Option>
                                <Select.Option key={Gender.Male} value={Gender.Male}>
                                    Männlich
                                </Select.Option>
                                <Select.Option key={Gender.Diverse} value={Gender.Diverse}>
                                    Divers
                                </Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item label='Vorname' validateStatus={validateStatus("firstname")} help={help("firstname")} style={{ gridColumn: "1" }} required>
                            <Input
                                data-testid='AttendeeCreateModalFirstName'
                                // disabled={state === AttendeeModalState.BookerModalOpen}
                                onChange={handleTextInputChange("firstname")}
                                onBlur={handleBlur("firstname")}
                                value={values.firstname}
                            />
                        </Form.Item>
                        <Form.Item label='Nachname' validateStatus={validateStatus("lastname")} help={help("lastname")} style={{ gridColumn: "1" }} required>
                            <Input
                                data-testid='AttendeeCreateModalLastName'
                                // disabled={state === AttendeeModalState.BookerModalOpen}
                                onChange={handleTextInputChange("lastname")}
                                onBlur={handleBlur("lastname")}
                                value={values.lastname}
                            />
                        </Form.Item>
                        <Form.Item
                            label='Geburtsdatum'
                            validateStatus={validateStatus("birthday")}
                            help={help("birthday")}
                            // style={{ gridColumn: "1" }}
                            style={{ width: "100%" }}
                            required
                        >
                            <BirthdayInput
                                data-testid='AttendeeCreateModalBirthday'
                                size='middle'
                                style={{ width: "100%" }}
                                onChange={(e) => safeSetFieldValue("birthday", e.target.value || undefined)}
                                onBlur={handleBlur("birthday")}
                                value={isDefined(values.birthday) ? moment(values.birthday).format("YYYY-MM-DD") : ""}
                                status={formikProps.values.birthday && formikProps.errors.birthday ? "error" : undefined}
                            />
                        </Form.Item>
                        {isDefined(duplicateMatch) && !duplicateMatch.allreadyBooked && (
                            <Alert
                                data-testid='duplicate-attendee-alert'
                                type='info'
                                showIcon
                                style={{ gridColumn: "1" }}
                                message={t("createEditAttendee.duplicate.alreadyExists", {
                                    name: `${duplicateMatch.attendee.firstname} ${duplicateMatch.attendee.lastname}`,
                                    birthday: formatBirthday(duplicateMatch.attendee.birthday),
                                })}
                                action={
                                    <Button
                                        data-testid='duplicate-attendee-use-existing'
                                        type='primary'
                                        size='small'
                                        onClick={() => handleUseExisting(duplicateMatch.attendee)}
                                    >
                                        {t("createEditAttendee.duplicate.continueWith", { firstname: duplicateMatch.attendee.firstname })}
                                    </Button>
                                }
                            />
                        )}
                        {isDefined(duplicateMatch) && duplicateMatch.allreadyBooked && (
                            <Alert
                                data-testid='duplicate-attendee-already-booked-alert'
                                type='warning'
                                showIcon
                                style={{ gridColumn: "1" }}
                                message={t("createEditAttendee.duplicate.alreadyBookedInCourse", { firstname: duplicateMatch.attendee.firstname })}
                            />
                        )}
                        <Space
                            className={css.footer}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <Button block danger onClick={() => handleCancle()} style={{ flex: 1 }}>
                                Abbrechen
                            </Button>
                            <Button
                                data-testid='attendee-modal-submit-button'
                                block
                                type='primary'
                                onClick={() => handleOk()}
                                disabled={(!isValid && !updatingBooker) || isDefined(duplicateMatch)}
                                style={{ flex: 1 }}
                                loading={updatingBooker}
                            >
                                {editMode ? "Teilnehmer speichern" : "Teilnehmer anlegen"}
                            </Button>
                        </Space>
                    </LayoutForm>
                )
            }}
        </Formik>
    )
}

export default memo(CreateEditAttendee)
